import { useState, useCallback, useEffect } from 'react';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';

// Rough bounding box for Ontario, Canada — used only when country is 'ca'.
const ONTARIO_BOUNDS = {
  sw: { lat: 41.6765, lng: -95.1562 },
  ne: { lat: 56.8591, lng: -74.3440 },
};

/**
 * School autocomplete — universities and colleges.
 *
 * - Defaults to Canada + Ontario bounds (for the onboarding flow).
 * - When `countries` is overridden (e.g. ['us'] or ['gb'] for the expansion
 *   dialog), bounds are dropped so the country itself becomes the only filter.
 */
const SchoolAutocomplete = ({
  onSelect,
  placeholder = 'Search your school in Ontario…',
  value = '',
  className = '',
  disabled = false,
  countries: countriesProp,
}) => {
  const [displayValue, setDisplayValue] = useState(value);

  // Keep displayValue in sync if parent resets `value`
  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  const countries = countriesProp || ['ca'];
  const isCanada = countries.length === 1 && countries[0] === 'ca';

  const handleSelect = useCallback(
    (place) => {
      setDisplayValue(place.name);
      if (onSelect) {
        onSelect({
          name: place.name,
          place_id: place.place_id,
          lat: place.lat,
          lng: place.lng,
          city: place.city,
          state: place.state,
          state_code: place.state_code,
          country: place.country,
          country_code: place.country_code,
          formatted_address: place.formatted_address,
        });
      }
    },
    [onSelect]
  );

  return (
    <GooglePlacesAutocomplete
      type="school"
      placeholder={placeholder}
      onSelect={handleSelect}
      value={displayValue}
      onChange={setDisplayValue}
      className={className}
      disabled={disabled}
      countries={countries}
      bounds={isCanada ? ONTARIO_BOUNDS : null}
    />
  );
};

export default SchoolAutocomplete;
